import React, { useEffect, useRef, useState } from 'react';

function AnimatedNumber({ value, duration = 1500, suffix = '', startOnView = false, ...props }) {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef();
  const startTimestamp = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    setDisplayValue(0);
    setHasAnimated(false);
  }, [value]);

  useEffect(() => {
    if (!startOnView) {
      startAnimation();
      return () => cancelAnimationFrame(raf.current);
    }

    let observer;
    if (ref.current && !hasAnimated) {
      observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setHasAnimated(true);
            startAnimation();
            if (observer) observer.disconnect();
          }
        },
        { threshold: 0.2 }
      );
      observer.observe(ref.current);
    }
    return () => {
      if (observer && ref.current) observer.unobserve(ref.current);
    };
    // eslint-disable-next-line
  }, [value, duration, startOnView, hasAnimated]);

  function startAnimation() {
    setDisplayValue(0);
    startTimestamp.current = null;
    function animate(timestamp) {
      if (!startTimestamp.current) startTimestamp.current = timestamp;
      const progress = Math.min((timestamp - startTimestamp.current) / duration, 1);
      const current = Math.floor(progress * value);
      setDisplayValue(current);
      if (progress < 1) {
        raf.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    }
    raf.current = requestAnimationFrame(animate);
  }

  return (
    <span ref={startOnView ? ref : undefined} {...props}>{displayValue}{suffix}</span>
  );
}

export default AnimatedNumber;
